import { useEffect, useState } from 'react';
import type { TourStep } from '@/types/product-tour';

interface TourSpotlightProps {
  step: TourStep;
  isActive: boolean;
}

export function TourSpotlight({ step, isActive }: TourSpotlightProps) {
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!isActive || !step.target) {
      setTargetRect(null);
      return;
    }

    const updateTargetRect = () => {
      const element = document.querySelector(step.target!);
      if (element) {
        setTargetRect(element.getBoundingClientRect());
      }
    };

    // Initial update
    updateTargetRect();

    // Update on scroll and resize
    window.addEventListener('scroll', updateTargetRect, true);
    window.addEventListener('resize', updateTargetRect);

    return () => {
      window.removeEventListener('scroll', updateTargetRect, true);
      window.removeEventListener('resize', updateTargetRect);
    };
  }, [step.target, isActive]);

  if (!isActive || !targetRect) {
    return null;
  }

  const padding = step.spotlightPadding || 8;

  return (
    <>
      {/* Overlay backdrop */}
      <div
        className="fixed inset-0 bg-black/60 transition-opacity duration-300 pointer-events-none"
        style={{ zIndex: 9998 }}
      />
      
      {/* Spotlight cutout */}
      <div
        className="fixed transition-all duration-300 pointer-events-none"
        style={{
          zIndex: 9999,
          top: targetRect.top - padding,
          left: targetRect.left - padding,
          width: targetRect.width + padding * 2,
          height: targetRect.height + padding * 2,
          boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6)',
          borderRadius: '8px',
        }}
      />

      {/* Pulsing border */}
      <div
        className="fixed transition-all duration-300 pointer-events-none animate-pulse"
        style={{
          zIndex: 10000,
          top: targetRect.top - padding,
          left: targetRect.left - padding,
          width: targetRect.width + padding * 2,
          height: targetRect.height + padding * 2,
          border: '2px solid hsl(var(--primary))',
          borderRadius: '8px',
        }}
      />
    </>
  );
}
